import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { Plus, ShieldAlert, Clock, Bell, Frown, ChevronRight, X } from 'lucide-react';

interface EscalationRule { id: string; rule_name: string; trigger_type: string; delay_minutes: number; notify_role: string; notify_email: string; notify_phone: string; priority: number; active: boolean; }

const TRIGGER_TYPES = [
  { value: 'sos', label: 'SOS Alert', description: 'When resident presses SOS', icon: 'ShieldAlert' },
  { value: 'missed_checkin', label: 'Missed Check-in', description: 'When daily check-in is missed', icon: 'Clock' },
  { value: 'missed_reminder', label: 'Missed Reminder', description: 'When reminder is not completed', icon: 'Bell' },
  { value: 'mood_low', label: 'Low Mood', description: 'When mood drops below threshold', icon: 'Frown' },
];

const TRIGGER_MAP: Record<string, string> = Object.fromEntries(TRIGGER_TYPES.map(t => [t.value, t.label])) as Record<string, string>;

const NOTIFY_ROLES = [
  { value: 'nurse', label: 'On-call Nurse' },
  { value: 'coordinator', label: 'Care Coordinator' },
  { value: 'family', label: 'Family Member' },
  { value: 'admin', label: 'Facility Admin' },
  { value: 'emergency', label: 'Emergency Services' },
];

export function EscalationRulesPage() {
  const { user } = useAuth();
  const [rules, setRules] = useState<EscalationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formTrigger, setFormTrigger] = useState('sos');
  const [formRole, setFormRole] = useState('nurse');
  const [formDelay, setFormDelay] = useState(0);
  const [formPriority, setFormPriority] = useState(1);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadRules(); }, [user]);

  const loadRules = async () => {
    if (!user) return;
    if (!supabase) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase.from('profiles').select('tenant_id').eq('id', user.id).single();
      if (!profile?.tenant_id) { setLoading(false); return; }
      setTenantId(profile.tenant_id);
      console.log('[Escalation] tenant_id:', profile.tenant_id);
      const { data, error: fetchErr } = await supabase.from('escalation_rules').select('*').eq('tenant_id', profile.tenant_id).order('trigger_type').order('priority');
      if (fetchErr) console.error('[Escalation] fetch error:', fetchErr);      
console.log('[Escalation] fetched rules:', data?.length, 'trigger_types:', data?.map((r: EscalationRule) => r.trigger_type));
      if (data) setRules(data);
    } catch (err) { console.error('[Escalation] Error:', err); }
    finally { setLoading(false); }
  };

  const saveRule = async () => {
    if (!tenantId || !formName.trim()) return;
    if (!supabase) return;
    setSaving(true);
    console.log('[Escalation] saving rule:', { tenant_id: tenantId, trigger_type: formTrigger.toLowerCase(), priority: formPriority });
    const dup = rules.find(r => r.trigger_type === formTrigger.toLowerCase() && r.priority === formPriority);
    if (dup) { setSaving(false); return; }
    try {
      await supabase.from('escalation_rules').insert({ tenant_id: tenantId, rule_name: formName.trim(), trigger_type: formTrigger.toLowerCase(), delay_minutes: formDelay, notify_role: formRole, notify_email: '', notify_phone: '', priority: formPriority, active: true });
      setShowForm(false);
      setFormName(''); setFormDelay(0); setFormPriority(1); setFormRole('nurse'); setFormTrigger('sos');
      loadRules();
    } catch (err) { console.error('[Escalation] Save error:', err); }
    finally { setSaving(false); }
  };

  const deleteRule = async (id: string) => {
    if (!supabase) return;
    await supabase.from('escalation_rules').delete().eq('id', id);
    loadRules();
  };

  const iconMap: Record<string, any> = { ShieldAlert, Clock, Bell, Frown };
  const getRulesForTrigger = (trigger: string) => rules.filter(r => r.trigger_type === trigger).sort((a, b) => a.priority - b.priority);
  const getRoleLabel = (value: string) => NOTIFY_ROLES.find(r => r.value === value)?.label || value;

  if (loading) return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">
      <div className="flex items-center justify-center h-[60vh] text-[rgb(var(--admin-text-muted))] text-base">Loading escalation rules...</div>
    </main>
  );

  return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold text-[rgb(var(--admin-text-primary))]">Alert Escalation Rules</h1>
          <p className="text-sm text-[rgb(var(--admin-text-muted))]">Define who gets notified when alerts occur</p>
        </div>
        <button onClick={() => setShowForm(true)} className="px-5 py-3 rounded-2xl bg-[rgb(var(--admin-accent))] text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98] flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Rule
        </button>
      </div>

      {/* How Escalation Works */}
      <div className="rounded-2xl p-4 bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))]">
        <p className="text-sm font-medium text-[rgb(var(--admin-text-primary))] mb-2">How Escalation Works</p>
        <p className="text-sm text-[rgb(var(--admin-text-secondary))]">When an alert is triggered, contacts are notified in priority order (1 first). Each step can have a delay. If no one responds, the system escalates to the next contact.</p>
        <p className="text-sm text-[rgb(var(--admin-text-muted))] mt-2">Example: Nurse (0 min) &rarr; Coordinator (5 min) &rarr; Family (15 min)</p>
      </div>

      {/* Add Rule Form */}
      {showForm && (
        <div className="rounded-3xl p-6 bg-[rgb(var(--admin-surface))] border-2 border-[rgb(var(--admin-accent))] shadow-warm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-[rgb(var(--admin-text-primary))]">New Escalation Rule</h3>
            <button onClick={() => { setShowForm(false); }} className="text-[rgb(var(--admin-text-muted))] hover:opacity-70"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Rule Name *</label>
              <input value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. SOS to Nurse" className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] text-sm" />
            </div>
            <div>
              <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Trigger</label>
              <select value={formTrigger} onChange={e => setFormTrigger(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] text-sm">
                {TRIGGER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Notify Role</label>
              <select value={formRole} onChange={e => setFormRole(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] text-sm">
                {NOTIFY_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Delay (min)</label>
                <input type="number" min={0} value={formDelay} onChange={e => setFormDelay(Number(e.target.value))} className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] text-sm" />
              </div>
              <div>
                <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Priority</label>
                <input type="number" min={1} value={formPriority} onChange={e => setFormPriority(Number(e.target.value))} className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] text-sm" />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={saveRule} disabled={saving || !formName.trim()} className="px-5 py-3 rounded-2xl bg-[rgb(var(--admin-accent))] text-white text-sm hover:opacity-90 transition-all duration-200 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Rule'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-2xl text-sm text-[rgb(var(--admin-text-muted))] hover:opacity-70">Cancel</button>
          </div>
        </div>
      )}

      {/* Trigger Cards */}
      <div className="space-y-4">
        {TRIGGER_TYPES.map(trigger => {
          const triggerRules = getRulesForTrigger(trigger.value);
          console.log('[Escalation] Rendering', trigger.label, 'key:', trigger.value, 'rules:', triggerRules.length, triggerRules);
          const Icon = iconMap[trigger.icon];
          return (
            <div key={trigger.value} className="rounded-3xl p-5 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm space-y-4 transition-all duration-200 hover:bg-[rgb(var(--admin-surface-2))]">
              {/* Trigger Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {Icon && <Icon className="w-5 h-5 text-[rgb(var(--admin-text-muted))]" />}
                  <div>
                    <p className="text-lg font-medium text-[rgb(var(--admin-text-primary))]">{trigger.label}</p>
                    <p className="text-sm text-[rgb(var(--admin-text-muted))]">{trigger.description}</p>
                  </div>
                </div>
                <button onClick={() => { setFormTrigger(trigger.value); setShowForm(true); }} className="px-4 py-2 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-sm text-[rgb(var(--admin-text-primary))] hover:opacity-80 transition-all duration-200 flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add Rule
                </button>
              </div>

              {/* Rule Chain */}
              {triggerRules.length > 0 ? (
                <div className="flex items-center gap-2 flex-wrap">
                  {triggerRules.map((rule, idx) => (
                    <div key={rule.id} className="flex items-center gap-2">
                      <div className="px-4 py-3 rounded-2xl bg-[rgb(var(--admin-surface-2))] border border-[rgb(var(--admin-border))] text-sm group relative">
                      <p className="font-medium text-[rgb(var(--admin-text-primary))]">L{rule.priority} — {getRoleLabel(rule.notify_role)} — {rule.delay_minutes === 0 ? 'immediately' : `after ${rule.delay_minutes} min`}</p>
                        <button onClick={() => deleteRule(rule.id)} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] text-[rgb(var(--admin-text-muted))] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs hover:text-[rgb(var(--admin-danger))]">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      {idx < triggerRules.length - 1 && (
                        <ChevronRight className="w-4 h-4 text-[rgb(var(--admin-text-muted))]" />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <p className="text-sm text-[rgb(var(--admin-text-muted))]">No escalation rules configured</p>
                  <button onClick={() => { setFormTrigger(trigger.value); setShowForm(true); }} className="px-3 py-1.5 rounded-xl bg-[rgb(var(--admin-surface-2))] text-xs text-[rgb(var(--admin-text-secondary))] hover:opacity-80 transition-all duration-200">
                    Add first rule
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

    </main>
  );
}